import { z } from "zod";

import { AudienceFilterList } from "../../dtos/audiences";
import { OrganizationDtoSchema } from "../../dtos/organizations";
import { AnalyzeResourceResponseBodyType } from "../../functions/analytics";
import { castToBool, castToNumber, castToString } from "../../utils";

export const DeviceTypeList = ["any", "mobile", "desktop"] as const;
export type DeviceType = (typeof DeviceTypeList)[number];

export const AnalyticsResourceListSchema = [
  "campaigns",
  "experiences",
  "sitewide",
] as const;
export type AnalyticsResourceType =
  (typeof AnalyticsResourceListSchema)[number];

export const VisitorTypeSchema = ["any", "new", "returning"] as const;
export type VisitorType = (typeof VisitorTypeSchema)[number];

export const AnalyzeContext = z.object({
  org: OrganizationDtoSchema,
  latestRunTs: z.string(),
  cacheInterval: z.number(),
});

export const PeriodTypeList = [
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "year",
] as const;
export type PeriodType = (typeof PeriodTypeList)[number];

export type AnalyzeContextType = z.infer<typeof AnalyzeContext>;

export const SubscriptionTypeSchema = ["any", "One Time", "Recurring"] as const;
export type SubscriptionAnalyticsType = (typeof SubscriptionTypeSchema)[number];

export const ChannelTypeSchema = [
  "Referral",
  "All Social",
  "Organic Social",
  "Paid Social",
  "Organic Search",
  "Paid Search",
  "Email",
  "Google",
  "Facebook",
  "Instagram",
  "Linktree",
  "TikTok",
  "Twitter",
  "Direct",
  "SMS",
  "Other",
  "Meta",
  "Link Tree",
  "Wunderkind Text",
  "Youtube",
] as const;
export type ChannelType = (typeof ChannelTypeSchema)[number];

export const UrlParamPayloadSchema = z.object({
  queryParam: z.string().nullable(),
  queryParamValue: z.string().nullable(),
  queryParamFilterType: z.enum(AudienceFilterList).nullable(),
});

export type UrlParamPayloadType = z.infer<typeof UrlParamPayloadSchema>;

export const DatasetIdList = [
  "overview",
  "aovHistogram",
  "freeShipping",
  "shippingMethod",
  "viewsOverTime",
  "cartProgression",
  "newReturningCustomers",
  "productGroups",
  "discounts",
  "discountHistogram",
  "ordersOverTime",
  "unitQuantityComposition",
  "unitQuantityHistogram",
  "pageViews",
  "ordersExport",
  "lineItemsExport",
  "campaignTotals",
  "campaignOverTime",
  "singleCampaignTotal",
  "cumulativeRevenueProfitOverTime",
  "customEventGoals",
  "customEventOptions",
  "countryCodeOptions",
  "allExperimentsOrdersVisitors",
  "subscriptions",
  "alwaysOnCumulative",
  "alwaysOnInterval",
  "ordersReconciliation",
  "ordersReconciliationExport",
  "aiProfit",
  "compareChannel",
  "compareNewReturning",
  "compareDeviceType",
] as const;

export type DatasetId = (typeof DatasetIdList)[number];

export const BaseAnalyticsPayloadSchema = z.object({
  datasetId: z.enum(DatasetIdList),
  startTime: castToString.or(z.number()).nullable(),
  endTime: castToString.or(z.number()).nullable(),
  shopifyDomain: z.string(),
  latestRunTs: z.string(),
  timezone: z.string(),
  urlParam: UrlParamPayloadSchema,
  deviceType: z.enum(DeviceTypeList).nullable(),
  visitorType: z.enum(VisitorTypeSchema).nullable(),
  channels: z.enum(ChannelTypeSchema).array(),
  shippingExpensePerOrder: castToNumber.or(z.string()).nullable(),
  transactionFeePct: castToNumber.or(z.string()).nullable(),
  resourceType: z.enum(AnalyticsResourceListSchema),
});

export type BaseAnalyticsRequestBodyType = z.infer<
  typeof BaseAnalyticsPayloadSchema
>;

export type CastedTimes = {
  startTime: string | number | undefined | null;
  endTime: string | number | undefined | null;
};

export const AnalyticsViewTypeList = [
  "test_orders_only",
  "sitewide_orders",
  "none",
] as const;
export type AnalyticsViewType = (typeof AnalyticsViewTypeList)[number];

export const StatSigViewSchema = ["best", "beatControl"] as const;
export type StatSigViewType = (typeof StatSigViewSchema)[number];

export const UserBehaviorFilterSchema = z.object({
  viewedCheckout: z.boolean().nullish(),
  addedToCart: z.boolean().nullish(),
});
export type UserBehaviorFilterType = z.infer<typeof UserBehaviorFilterSchema>;

export const AnalyzeAllCampaignsRequestPathParamSchema = z.object({
  datasetId: z.enum(DatasetIdList),
  resourceType: z.enum(AnalyticsResourceListSchema),
});
export type AnalyzeAllCampaignsRequestPathParamType = z.infer<
  typeof AnalyzeAllCampaignsRequestPathParamSchema
>;

const ProbabilityRequestTestGroupSchema = z.object({
  variation_name: z.string(),
  is_control: z.boolean(),
  visitors: z.number(),
  orders: z.number(),
  total_value: z.number(),
});

const LossProbabilitiesSchema = z.object({
  VARIATION_NAME: z.string(),
  tolerance: z.number(),
  value_per_visitor_best: z.number(),
  value_per_visitor_beat_control: z.number(),
});

export type LossProbabilitiesType = z.infer<typeof LossProbabilitiesSchema>;

const ProbabilitiesRequestSchema = ProbabilityRequestTestGroupSchema.array();

export type ProbabilitiesRequestType = z.infer<
  typeof ProbabilitiesRequestSchema
>;

export const AnalyzeResourceDatasetOptionsSchema = z.object({
  id: z.string().uuid().optional(),
  datasetId: z.enum(DatasetIdList),
  resourceType: z.enum(AnalyticsResourceListSchema),
  testProductsOnly: castToBool.optional(),
  isTestOrdersOnly: castToBool,
  shippingExpensePerOrder: castToNumber.optional(),
  transactionFeePercent: z.number().optional(),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
  discountIncludeType: castToNumber.optional(),
  deviceType: z.enum(DeviceTypeList).optional(),
  channelTypes: z.string().array().optional(),
  visitorType: z.enum(VisitorTypeSchema).optional(),
  subscriptionType: z.enum(SubscriptionTypeSchema).optional(),
  maxZScore: z.number().optional(),
  customEvents: z.string().array().optional(),
  countryCodes: z.string().array().optional(),
  addedToCart: z.boolean().optional(),
  viewedCheckout: z.boolean().optional(),
  period: z.enum(PeriodTypeList).optional(),
  urlParam: z
    .object({
      queryParam: z.string(),
      queryParamValue: z.string(),
      queryParamFilterType: z.enum(AudienceFilterList),
    })
    .optional(),
  latestRunTs: z.number(),
  cacheInterval: z.number(),
  org: OrganizationDtoSchema,
});

export type AnalyzeResourceDatasetOptionsType = z.infer<
  typeof AnalyzeResourceDatasetOptionsSchema
>;

export const AnalyzeExperimentSummaryOptionsSchema = z.object({
  shopifyDomain: z.string().optional(),
  experimentId: z.string(),
  startTs: z.number().optional(),
  endTs: z.number().optional(),
  includeProductFilter: z.boolean(),
  explicitlyExclude: z.boolean(),
});
export type AnalyzeExperimentSummaryOptionsType = z.infer<
  typeof AnalyzeExperimentSummaryOptionsSchema
>;

export type AnalyticsResponsePayload = {
  metrics: AnalyzeResourceResponseBodyType["metrics"];
  loss: AnalyzeResourceResponseBodyType["loss"];
};
